import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { COLORS } from 'theme/MainTheme';
import { type Message } from 'types/Message';
import moment from 'moment';
import 'moment/locale/fr';
import MessageBodyPreviewLoader from './MessageBodyPreviewLoader';
import { AdminMessagesTableDataTypeEnum } from './AdminMessagesTableData';
import SelectMessageCheckbox from './SelectMessageCheckbox';
import clsx from 'clsx';
import {Paperclip} from 'components/icons';

moment.locale('fr');

export const formatDate = (date: number): string => moment(date).format('D MMM YYYY');

export const getFormattedEmailLabel = (
  message: Message,
  type: AdminMessagesTableDataTypeEnum,
): string => {
  if (type === AdminMessagesTableDataTypeEnum.SENT) {
    return message.destAddress
      .map((_) => {
        const parts = _.split('@');
        return parts[0];
      })
      .join(', ');
  }

  return message.senderAddress;
};

const useStyles = makeStyles(() => ({
  bold: {
    fontWeight: 'bold',
  },
  clickableTd: {
    cursor: 'pointer',
  },
  dateTd: {
    textTransform: 'capitalize',
  },
  emailAddressTd: {
    fontSize: 15,
    fontWeight: 500,
    maxWidth: 280,

    '&.bold': {
      fontSize: 16,
      fontWeight: 'bold',
    },
  },
  message: {
    fontWeight: 300,
    overflow: 'hidden',
    paddingLeft: 10,
    textOverflow: 'ellipsis',
  },
  messageContainer: {
    maxWidth: 600,
  },
  row: {
    '&:hover': {
      backgroundColor: COLORS.LIGHT_BLUE,
    },
  },
}));


type AdminMessagesTableRowProps = {
  message: Message;
  type: AdminMessagesTableDataTypeEnum;
  handleRowClick: (message: Message) => void;
};

const AdminMessagesTableRow = ({ message, type, handleRowClick }: AdminMessagesTableRowProps): JSX.Element => {
  const classes = useStyles();

  const handleClick = () => handleRowClick(message);

  return (
    <TableRow
      className={classes.row}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell>
        <SelectMessageCheckbox message={message} />
      </TableCell>

      <TableCell
        className={clsx(
          classes.clickableTd,
          classes.emailAddressTd,
          !message.read && 'bold',
        )}
        onClick={handleClick}
      >
        {getFormattedEmailLabel(message, type)}
      </TableCell>

      <TableCell
        className={classes.clickableTd}
        onClick={handleClick}
      >
        {message.hasAttachment && <Paperclip />}
      </TableCell>

      <TableCell
        className={clsx(classes.clickableTd, classes.messageContainer)}
        onClick={handleClick}
      >
        <span className={clsx([!message.read && classes.bold])}>
          {message.subject}
        </span>

        <span className={classes.message}>
          <MessageBodyPreviewLoader message={message} />
        </span>
      </TableCell>

      <TableCell
        align="right"
        className={clsx(classes.clickableTd, classes.dateTd)}
        onClick={handleClick}
      >
        {formatDate(message.date)}
      </TableCell>
    </TableRow>
  );
};

export default AdminMessagesTableRow;